/**
 * Serviço centralizado para execução de ferramentas VB no CorelDRAW
 * @module VBCompilerService
 * 
 * Este serviço gerencia a execução de código VB com:
 * - Timeout customizado (20 minutos padrão)
 * - Progress updates em tempo real
 * - Suporte a cancelamento
 * - Registro automático de execução
 */

import { corelCommunication } from '../communication';
import { debugLog, sendToTerminal, sendToTestLog } from '../../terminalLogger';
import { LogCategory } from '../../../config/logging';
import type {
  VBCompilerPayload,
  VBCompilerOptions,
  VBCompilerProgress,
  VBCompilerResponse,
  VBExecutionResult,
  ToolData
} from './vbcompiler.types';

/**
 * Timeout padrão para operações VB Compiler (20 minutos)
 */
export const VBCOMPILER_TIMEOUT_MS = 20 * 60 * 1000;

/**
 * Serviço centralizado para VB Compiler
 */
export class VBCompilerService {
  /**
   * Executa uma ferramenta VB no CorelDRAW
   * 
   * @param payload Payload de execução
   * @param options Opções de configuração
   * @returns Promise com resultado completo
   */
  static async execute(
    payload: VBCompilerPayload,
    options?: VBCompilerOptions
  ): Promise<VBExecutionResult> {
    const startTime = Date.now();
    const timeoutMs = options?.timeoutMs ?? VBCOMPILER_TIMEOUT_MS;

    debugLog('INFO', {
      scope: 'VBCompilerService.execute',
      toolId: payload.toolId,
      timeoutMs
    });

    return new Promise((resolve) => {
      let requestId: string | null = null;
      let timedOut = false;

      // Timeout principal
      const timeoutId = setTimeout(() => {
        timedOut = true;
        sendToTerminal('⏱️ [VBCOMPILER] Timeout atingido', {
          toolId: payload.toolId,
          elapsedMs: Date.now() - startTime
        });

        resolve({
          success: false,
          status: 'timeout',
          error: 'Timeout ao executar ferramenta VB',
          elapsedMs: Date.now() - startTime
        });
      }, timeoutMs);

      try {
        // Log do payload completo antes de enviar (para debug)
        sendToTerminal('🔍 [VBCOMPILER] Payload antes de enviar', {
          action: 'vbcompiler',
          payload: JSON.parse(JSON.stringify(payload)), // Clonar para evitar referência
          hasDisablePerformanceMonitor: 'disablePerformanceMonitor' in payload,
          disablePerformanceMonitorValue: payload.disablePerformanceMonitor
        });
        
        requestId = corelCommunication.sendActionGuarded(
          'vbcompiler',
          payload as any,
          (response: any) => {
            if (timedOut) return; // Ignora se já passou do timeout

            // Progress update
            if (response?.status === 'progress_update') {
              // ✅ Log de progress update para canal csharp
              sendToTestLog('VBCOMPILER:PROGRESS', {
                action: 'vbcompiler',
                requestId: response?.requestId || requestId || 'unknown',
                progress: {
                  current: response?.result?.current ?? response?.data?.current ?? 0,
                  total: response?.result?.total ?? response?.data?.total ?? 0,
                  percent: response?.result?.percent ?? response?.data?.percent,
                  message: response?.result?.message ?? response?.data?.message
                }
              }, 'csharp', LogCategory.CONNECTION);
              const cur = response?.result?.current ?? response?.data?.current ?? 0;
              const tot = response?.result?.total ?? response?.data?.total ?? 0;
              const pct = response?.result?.percent ?? response?.data?.percent ?? 
                          (tot > 0 ? Math.round((cur / tot) * 100) : undefined);
              const msg = response?.result?.message ?? response?.data?.message;

              const progress: VBCompilerProgress = {
                current: cur,
                total: tot,
                percent: pct,
                message: msg
              };

              if (options?.onProgress) {
                try {
                  options.onProgress(progress);
                } catch (err) {
                  debugLog('ERROR', {
                    scope: 'VBCompilerService.execute.onProgress',
                    error: err instanceof Error ? err.message : String(err)
                  });
                }
              }

              return; // Não resolve ainda
            }

            // Conclusão
            clearTimeout(timeoutId);
            const elapsedMs = Date.now() - startTime;

            const status = response?.status as any;
            const success = status === 'ok';
            const cancelled = status === 'cancelled';
            const error = response?.error || response?.message;

            // ✅ Log de recebimento para canal csharp
            sendToTestLog('VBCOMPILER:RECV', {
              action: 'vbcompiler',
              requestId: response?.requestId || requestId || 'unknown',
              status,
              success,
              cancelled,
              elapsedMs,
              hasError: !!error,
              error: error || undefined,
              hasResult: !!response?.result,
              responseKeys: Object.keys(response || {})
            }, 'csharp', LogCategory.CONNECTION);

            debugLog('INFO', {
              scope: 'VBCompilerService.execute',
              status,
              success,
              elapsedMs
            });

            const result: VBExecutionResult = {
              success,
              requestId: response?.requestId || requestId || undefined,
              status: cancelled ? 'cancelled' : (success ? 'ok' : 'error'),
              response,
              error: success ? undefined : error,
              elapsedMs
            };

            // Callbacks
            if (cancelled && options?.onCancel) {
              try {
                options.onCancel();
              } catch {}
            } else if (!success && options?.onError) {
              try {
                options.onError(error || 'Erro desconhecido');
              } catch {}
            } else if (success && options?.onComplete) {
              try {
                options.onComplete(response);
              } catch {}
            }

            resolve(result);
          },
          timeoutMs
        );

        // ✅ Log de envio para canal csharp (após obter requestId)
        if (requestId) {
          // Log também no terminal para debug
          sendToTerminal('📤 [VBCOMPILER] Enviando para C#', {
            requestId,
            vbid: payload.vbid,
            disablePerformanceMonitor: payload.disablePerformanceMonitor
          });
          
          sendToTestLog('VBCOMPILER:SEND', {
            action: 'vbcompiler',
            requestId,
            payload: {
              vbid: payload.vbid,
              hasCode: !!payload.code,
              codeLength: payload.code?.length || 0,
              parameters: payload.parameters,
              progress: payload.progress,
              progressEvery: payload.progressEvery,
              progressIntervalMs: payload.progressIntervalMs,
              progressLabel: payload.progressLabel,
              progressStartEnd: payload.progressStartEnd,
              uiModal: payload.uiModal,
              uiCancelable: payload.uiCancelable,
              disablePerformanceMonitor: payload.disablePerformanceMonitor,
              // Campos importantes para debug
              hasDisablePerformanceMonitor: 'disablePerformanceMonitor' in payload,
              allPayloadKeys: Object.keys(payload),
              // Payload completo serializado (para verificar exatamente o que vai para C#)
              payloadJson: JSON.stringify(payload)
            }
          }, 'csharp', LogCategory.CONNECTION);
        }
        
        if (!requestId) {
          clearTimeout(timeoutId);
          resolve({
            success: false,
            status: 'error',
            error: 'Falha ao enviar comando vbcompiler',
            elapsedMs: Date.now() - startTime
          });
        }
        } catch (error) {
          clearTimeout(timeoutId);
          const errorMsg = error instanceof Error ? error.message : 'Erro desconhecido';
          
          // ✅ Log de erro para canal csharp
          sendToTestLog('VBCOMPILER:ERROR', {
            action: 'vbcompiler',
            requestId: requestId || 'unknown',
            error: errorMsg,
            errorType: error instanceof Error ? error.name : typeof error
          }, 'csharp', LogCategory.ERROR);
          
          sendToTerminal('❌ [VBCOMPILER] Exceção ao executar', {
          toolId: payload.toolId,
          error: errorMsg
        });

        resolve({
          success: false,
          status: 'error',
          error: errorMsg,
          elapsedMs: Date.now() - startTime
        });
      }
    });
  }

  /**
   * Executa ferramenta a partir de dados da API
   * 
   * Converte ToolData para VBCompilerPayload e executa
   * 
   * @param tool Dados da ferramenta (do API)
   * @param parameters Parâmetros de execução
   * @param options Opções de configuração
   * @returns Promise com resultado completo
   */
  static async executeFromTool(
    tool: ToolData,
    parameters: Record<string, any>,
    options?: VBCompilerOptions
  ): Promise<VBExecutionResult> {
    const payload: VBCompilerPayload = {
      toolId: tool.id,
      parameters,
      vbCode: tool.vbCode || '',
      progress: tool.config?.progress ?? true,
      progressEvery: tool.config?.progressEvery ?? 3,
      progressIntervalMs: tool.config?.progressIntervalMs ?? 150,
      progressLabel: tool.config?.progressLabel ?? 'Processamento',
      progressStartEnd: tool.config?.progressStartEnd ?? true,
      uiModal: tool.config?.uiModal ?? true,
      uiCancelable: tool.config?.uiCancelable ?? true
    };

    return this.execute(payload, options);
  }

  /**
   * Executa ferramenta usando formato alternativo (vbid + code)
   * 
   * @param vbid Identificador VB
   * @param code Código VB
   * @param parameters Parâmetros de execução
   * @param config Configuração adicional
   * @param options Opções de execução
   * @returns Promise com resultado completo
   */
  static async executeWithCode(
    vbid: string,
    code: string,
    parameters: Record<string, any>,
    config?: {
      progress?: boolean;
      progressEvery?: number;
      progressIntervalMs?: number;
      progressLabel?: string;
      progressStartEnd?: boolean;
      uiModal?: boolean;
      uiCancelable?: boolean;
      disablePerformanceMonitor?: boolean;
    },
    options?: VBCompilerOptions
  ): Promise<VBExecutionResult> {
    // ✅ Usar ?? para respeitar valores da API (false, 0, '') 
    // Fallback só é aplicado quando valor é null ou undefined
    const payload: VBCompilerPayload = {
      vbid,
      code,
      parameters,
      progress: config?.progress ?? true,
      progressEvery: config?.progressEvery ?? 3,
      progressIntervalMs: config?.progressIntervalMs ?? 150,
      progressLabel: config?.progressLabel ?? 'Processamento', // ✅ Corrigido: || → ??
      progressStartEnd: config?.progressStartEnd ?? true,
      uiModal: config?.uiModal ?? true,
      uiCancelable: config?.uiCancelable ?? true,
      disablePerformanceMonitor: config?.disablePerformanceMonitor ?? false
    };

    // Log detalhado do payload criado
    sendToTerminal('🔍 [VBCOMPILER] Payload criado em executeWithCode', {
      vbid,
      hasConfig: !!config,
      configReceived: config,
      payloadCreated: {
        ...payload,
        code: payload.code ? `${payload.code.substring(0, 50)}...` : 'empty'
      },
      hasDisablePerformanceMonitor: 'disablePerformanceMonitor' in payload,
      disablePerformanceMonitorValue: payload.disablePerformanceMonitor
    });

    return this.execute(payload, options);
  }

  /**
   * Normaliza resposta de progresso
   * 
   * @param response Resposta bruta
   * @returns Progresso normalizado
   */
  static normalizeProgress(response: any): VBCompilerProgress {
    const cur = response?.result?.current ?? response?.data?.current ?? 0;
    const tot = response?.result?.total ?? response?.data?.total ?? 0;
    const pct = response?.result?.percent ?? response?.data?.percent ?? 
                (tot > 0 ? Math.round((cur / tot) * 100) : undefined);
    const msg = response?.result?.message ?? response?.data?.message;

    return {
      current: cur,
      total: tot,
      percent: pct,
      message: msg
    };
  }
}

/**
 * Função auxiliar para compatibilidade
 * @deprecated Use VBCompilerService.execute() diretamente
 */
export const executeVBTool = VBCompilerService.execute;

